import { useMemo } from 'react';
import { Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ColumnGroup } from 'react-table';

import i18next from 'i18next';

import { warehouses } from 'constants/warehouses';

import { QuantitySize } from 'types/api/orders.type';

import { useAppSelector } from 'redux/hooks';

import { parsePrice } from 'helpers/parsePrice';

import { SizeInput } from 'components/SizeInput';

import { usePriceVisible } from './usePriceVisible';

type ScaleColumnsOptions = {
  scale?: string;
  colorCode?: string;
  quantities?: QuantitySize[];
  isAvailable?: boolean;
  isInOrder?: boolean;
  control?: Control;
  savingInputName?: string | null;
  onQuantityBlur?: (size: string, newQuantity: number) => Promise<void>;
};

export const useScaleColumns = (options: ScaleColumnsOptions) => {
  const { scale, isAvailable, isInOrder, control, savingInputName, onQuantityBlur } = options;

  const { warehouse } = useAppSelector((state) => state.settings);
  const isSimpleCatalogue = warehouse.code === warehouses[0].code;
  const { t } = useTranslation();
  const viewPrice = usePriceVisible();

  const isSaving = savingInputName !== null && savingInputName !== undefined;

  const columns = useMemo(
    (): ColumnGroup[] => [
      {
        Header: t('catalogue.sizes')!,
        columns: [
          {
            Header: `${t('catalogue.scale')!} ${scale}`,
            accessor: 'size',
          },
          ...(isInOrder
            ? [
                {
                  Header: t('order.qty')!,
                  accessor: 'orderQuantity',
                  Cell: ({ row }: { row: { original: Record<string, unknown> } }) => {
                    const size = row.original.size as string;
                    const qty = (row.original.orderQuantity as number) ?? 0;
                    const minQty = row.original.minQty as string;
                    const price = row.original.price as string;
                    const hasPrice = !!price;

                    if (!control || !onQuantityBlur) {
                      return <span>{qty}</span>;
                    }

                    return (
                      <SizeInput
                        name={`scale-${size}`}
                        control={control}
                        sizeQuantity={{
                          size,
                          quantity: qty,
                          minQuantity: minQty,
                          price,
                        }}
                        disabled={isSaving || !hasPrice}
                        onBlur={(newValue: number) => onQuantityBlur(size, newValue)}
                      />
                    );
                  },
                },
              ]
            : []),
          {
            Header: t('catalogue.minQty')!,
            accessor: 'minQty',
          },
          ...(viewPrice
            ? [
                {
                  Header: t('catalogue.unitPrice')!,
                  accessor: 'price',
                  Cell: ({
                    value,
                    row,
                  }: {
                    value: string;
                    row: { original: Record<string, unknown> };
                  }) => {
                    const currency = row.original.currency as string;
                    if (!value || !currency) return <span>—</span>;
                    const formatted = parsePrice(value).toFixed(2);
                    return <span>{`${currency} ${formatted}`}</span>;
                  },
                },
              ]
            : []),
          ...(isInOrder && viewPrice
            ? [
                {
                  Header: t('catalogue.total')!,
                  accessor: 'rowTotal',
                  Cell: ({ row }: { row: { original: Record<string, unknown> } }) => {
                    const qty = (row.original.orderQuantity as number) ?? 0;
                    const price = parsePrice((row.original.price as string) || '0');
                    const currency = row.original.currency as string;
                    const total = (price * qty).toFixed(2);

                    return <span>{qty > 0 ? `${currency} ${total}` : '—'}</span>;
                  },
                },
              ]
            : []),
        ],
      },
    ],
    [
      i18next.language,
      scale,
      isAvailable,
      isInOrder,
      isSaving,
      control,
      onQuantityBlur,
      viewPrice,
    ]
  );

  if (!isSimpleCatalogue) {
    const priceColIndex = columns[0].columns.findIndex((col) => col.accessor === 'price');
    const insertAt = priceColIndex >= 0 ? priceColIndex : columns[0].columns.length;
    columns[0].columns.splice(insertAt, 0, {
      Header: "Qta' Disp.",
      accessor: 'quantity',
      Cell: ({ value }: { value: number | string }) => <span>{value}</span>,
    });
  }

  return columns;
};
